import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { Surface, Text } from "@/shared/ui";
import type { RunSkill } from "../api/getRunSkills";
import { skillSlug } from "../lib/slashSkills";
import { BoltIcon } from "./icons";

export interface SkillSlashMenuProps {
  /** Skills matching the typed token, in display order. */
  skills: RunSkill[];
  /** Index of the keyboard-highlighted row. */
  activeIndex: number;
  onHover: (index: number) => void;
  /** Fill the draft with the chosen skill's slash token. */
  onSelect: (skill: RunSkill) => void;
}

/**
 * The composer's slash typeahead: a floating list of the user's skills, shown
 * while the draft opens with `/`. Selecting one (click / Enter) fills the draft
 * with `/<slug> ` and scopes the next turn to that skill. Purely presentational
 * — the open/filter/keyboard state lives in `Composer`.
 */
export function SkillSlashMenu({ skills, activeIndex, onHover, onSelect }: SkillSlashMenuProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);

  // Keep the highlighted row in view as the user arrows through.
  useEffect(() => {
    const el = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <Surface
      tone="panel-2"
      radius="md"
      border="none"
      shadow="pill"
      className="absolute bottom-full left-0 z-20 mb-2 max-h-[260px] w-[280px] overflow-hidden"
      role="listbox"
      aria-label={t("chat.skillSlash.label")}
    >
      <div className="px-3 pb-1 pt-2">
        <Text variant="label" as="div" className="text-[10.5px]">
          {t("chat.skillSlash.title")}
        </Text>
      </div>
      <ul ref={listRef} className="max-h-[212px] overflow-y-auto p-1.5">
        {skills.map((skill, index) => (
          <li key={skill.id}>
            <button
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              onMouseEnter={() => onHover(index)}
              onMouseDown={(e) => {
                // Keep the textarea focused — don't blur on the pointer-down.
                e.preventDefault();
                onSelect(skill);
              }}
              className={cn(
                "flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left outline-none transition-colors duration-100 ease-house",
                index === activeIndex ? "bg-line-soft" : "hover:bg-line-soft",
              )}
            >
              <span
                aria-hidden="true"
                className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-md bg-ink text-paper [&_svg]:size-[15px]"
              >
                <BoltIcon />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-sans text-[13.5px] font-medium text-ink">
                  {skill.name}
                </span>
                <span className="block truncate font-mono text-[11.5px] text-ink-faint">
                  /{skillSlug(skill.name)}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Surface>
  );
}
